// File: frontend/src/components/Modal/Modal.js

import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './Modal.module.css';

const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  variant = 'default',
  showCloseButton = true,
  hideHeader = false,
  disableBackdropClick = false
}) => {
  // Handle ESC key press to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && isOpen && !disableBackdropClick) {
      onClose();
    }
  }, [isOpen, onClose, disableBackdropClick]);

  useEffect(() => {
    if (isOpen) {
      // Prevent background scrolling when modal is open
      document.body.style.overflow = 'hidden';
      // Add event listener for ESC key
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  const handleBackdropClick = (e) => {
    if (!disableBackdropClick) {
      onClose(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div 
        className={`${styles.modalContent} ${styles[variant]}`} 
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "modal-title" : undefined}
      >
        {!hideHeader && (
          <div className={styles.modalHeader}>
            {title && <h3 id="modal-title" className={styles.modalTitle}>{title}</h3>}
            {showCloseButton && (
              <button
                type="button"
                className={styles.closeButton}
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}
        
        {/* If header is hidden but we still need a close button */}
        {hideHeader && showCloseButton && (
          <button
            type="button"
            className={`${styles.closeButton} ${styles.floatingCloseButton}`}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}
        
        <div className={`${styles.modalBody} ${hideHeader ? styles.noHeaderPadding : ''}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

Modal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'warning', 'error', 'info', 'success']),
  showCloseButton: PropTypes.bool,
  hideHeader: PropTypes.bool,
  disableBackdropClick: PropTypes.bool
};

export default Modal;